import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SystemEnergyState, EnergiesService } from 'ngx-core-services';
import { map, switchMap, catchError, withLatestFrom, mapTo, first } from 'rxjs/operators';
import { of, from } from 'rxjs';

import {
  AppState,
  SystemsState,
  getSystemDefinitionDeviceIdsForTelemetries,
  getSystemDefinitionConfiguration,
  requestSystemEnergyState,
  selectedSystemMetadata,
  setSystemEnergyStateError,
  updateSystemEnergyState,
} from '..';

@Injectable()
export class TotalizerEffects {

  requestSystemEnergyState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemEnergyState),
      switchMap((action) => {
        return this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        );
      }),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) => {
        return from(this.getSystemEnergyState(action.systemId, action.forceRefresh, systemState)).pipe(
          map((systemEnergyState: SystemEnergyState) => updateSystemEnergyState({ systemId: action.systemId, instanceId: action.instanceId, systemEnergyState })),
          catchError(_ => of(setSystemEnergyStateError({ systemId: action.systemId, instanceId: action.instanceId, error: 'Failed to fetch system energy state' })))
        );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly energiesService: EnergiesService,
  ) {}

  async getSystemEnergyState(systemId: string, forceRefresh: boolean, state: SystemsState): Promise<SystemEnergyState> {
    const system = state.systemList[systemId];

    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }

    const systemConfiguration = getSystemDefinitionConfiguration(system);

    /**
     * If the monitoringType is Connext, I only take the serials of the Connext type devices
     * Otherwise I take all devices except those of type Connext
     */
    const serials = getSystemDefinitionDeviceIdsForTelemetries(system);

    const energies = await this.energiesService.fetchVirtualEnergyTelemetry(serials, `${ systemConfiguration.tz }`, systemConfiguration.monitoringType, forceRefresh);
    const aggregateEnergy = this.energiesService.getSnapshotEnergyAggregate(energies, !!systemConfiguration.inverterMeterSensor);

    return {
      ET1: aggregateEnergy.ET1 ?? 0,
      ET2: aggregateEnergy.ET2 ?? 0,
      ET3: aggregateEnergy.ET3 ?? 0,
      ET4: aggregateEnergy.ET4 ?? 0,
      ET5: aggregateEnergy.ET5 ?? 0,
      ET6: aggregateEnergy.ET6 ?? 0,
      EG1: aggregateEnergy.EG1 ?? 0,
      EG2: aggregateEnergy.EG2 ?? 0,
      EG3: aggregateEnergy.EG3 ?? 0,
      EG4: aggregateEnergy.EG4 ?? 0,
      EG5: aggregateEnergy.EG5 ?? 0,
      EG6: aggregateEnergy.EG6 ?? 0,
      energyDailyGenerating: aggregateEnergy.EG1 ?? 0,
      energyDailyConsuming: aggregateEnergy.EG4 ?? 0,
      energyDailyCharging: aggregateEnergy.EG5 ?? 0,
      energyDailyDischarging: aggregateEnergy.EG6 ?? 0,
      energyDailyImporting: aggregateEnergy.EG3 ?? 0,
      energyDailyExporting: aggregateEnergy.EG2 ?? 0,
      energyTotalGenerating: aggregateEnergy.ET1 ?? 0,
      energyTotalConsuming: aggregateEnergy.ET4 ?? 0,
      energyTotalCharging: aggregateEnergy.ET5 ?? 0,
      energyTotalDischarging: aggregateEnergy.ET6 ?? 0,
      energyTotalImporting: aggregateEnergy.ET3 ?? 0,
      energyTotalExporting: aggregateEnergy.ET2 ?? 0,
    } as SystemEnergyState;
  }
}
